import { useCallback } from "react";
import { waitForUserConfigurationToInitialize } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	AppStoreEvent,
	subscribe as subscribeToAppStoreEvent
} from "client_legacy/Apps/AppStore/events";
import createVanilla, { StoreApi } from "zustand/vanilla";
import create, { UseStore } from "zustand";
import { AppType } from "client/Api/Apps/common";
import { shallowComparison } from "client/Misc/compareUtils";

export enum MainMenuItem {
	CreateItem = "create-item",
	Activity = "activity",
	ChatAssistant = "chat-assistant",
	NewFeatures = "new-features"
}

export type WorkspaceHandler = {
	openDefaultSpace(): void;
};

export type MainMenuStoreData = {
	readonly activeItem: MainMenuItem | null;
	readonly workspaceHandler: WorkspaceHandler | undefined;

	activateItem: (item: MainMenuItem) => void;
	deactivateItem: (item: MainMenuItem) => void;
	deactivate: () => void;
	setWorkspaceHandler: (workspaceHandler: WorkspaceHandler) => void;

	readonly isAddressBookAvailable: boolean;
	readonly isHubAppAvailable: boolean;
	readonly isDocumentsAppAvailable: boolean;
	readonly isAiAssistantAvailable: boolean;
};

const mainMenuStore: StoreApi<MainMenuStoreData> = createVanilla(
	(set): MainMenuStoreData => {
			waitForUserConfigurationToInitialize()
				.then(userConfiguration => {
					set(previousState => ({
						...previousState,
						isAddressBookAvailable: userConfiguration.addressBook
							.isAppAvailable,
						isHubAppAvailable: userConfiguration.isHubAppAvailable,
						isDocumentsAppAvailable: userConfiguration.document
							.isAppAvailable,
						isAiAssistantAvailable: userConfiguration
							.aiAssistant
							.isAiAssistantAvailable
					}));
				});

			subscribeToAppStoreEvent(AppStoreEvent.AppEnabled, (_, { type }) => {
				switch (type) {
					case AppType.AddressBook:
						set(previousState => ({
							...previousState,
							isAddressBookAvailable: true
						}));
						return;
					case AppType.Documents:
						set(previousState => ({
							...previousState,
							isDocumentsAppAvailable: true
						}));
						return;
					case AppType.Hub:
						set(previousState => ({
							...previousState,
							isHubAppAvailable: true
						}));
						return;
					default:
						break;
				}
			});

			subscribeToAppStoreEvent(AppStoreEvent.AppDisabled, (_, { type }) => {
				switch (type) {
					case AppType.AddressBook:
						set(previousState => ({
							...previousState,
							isAddressBookAvailable: false
						}));
						return;
					case AppType.Documents:
						set(previousState => ({
							...previousState,
							isDocumentsAppAvailable: false
						}));
						return;
					case AppType.Hub:
						set(previousState => ({
							...previousState,
							isHubAppAvailable: false
						}));
						return;
					default:
						break;
				}
			});

			return {
				activeItem: null,
				workspaceHandler: undefined,
				isAddressBookAvailable: false,
				isHubAppAvailable: false,
				isDocumentsAppAvailable: false,
				isAiAssistantAvailable: false,
				activateItem(item: MainMenuItem): void {
					set(previousState => {
						if (previousState.activeItem === item) {
							return previousState;
						}
						return { ...previousState, activeItem: item };
					});
				},
				deactivateItem(item: MainMenuItem): void {
					set(previousState => {
						if (previousState.activeItem !== item) {
							return previousState;
						}
						return {
							...previousState,
							activeItem: null
						};
					});
				},
				deactivate(): void {
					set({ activeItem: null });
				},
				setWorkspaceHandler(workspaceHandler: WorkspaceHandler): void {
					set({ workspaceHandler });
				},
			};
		}
);

export const useMainMenuStore: UseStore<MainMenuStoreData> = create<MainMenuStoreData>(mainMenuStore);

type MainMenuItemActivationHandlers = {
	readonly isActive: boolean;
	activate(): void;
	deactivate(): void;
	toggle(): void;
};

export function useMainMenuItemActivationHandlers<TItem extends MainMenuItem>(
	item: TItem
): MainMenuItemActivationHandlers {
	const {
		activeItem,
		activateItem,
		deactivateItem
	} = useMainMenuStore(
		s => ({
			activeItem: s.activeItem,
			activateItem: s.activateItem,
			deactivateItem: s.deactivateItem
		}),
		shallowComparison
	);

	const isActive: boolean = activeItem === item;

	const activateHandler = useCallback(
		() => activateItem(item),
		[activateItem, item]
	);

	const deactivateHandler = useCallback(
		() => deactivateItem(item),
		[deactivateItem, item]
	);

	const toggleHandler = useCallback(
		(): void => {
			if (isActive) {
				deactivateHandler();
			} else {
				activateHandler();
			}
		},
		[activateHandler, deactivateHandler, isActive]
	);

	return {
		isActive: isActive,
		activate: activateHandler,
		deactivate: deactivateHandler,
		toggle: toggleHandler
	};
}